import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import useApi from '../hooks/useApi';

const SocialStatusContext = createContext();

export const useSocialStatus = () => {
  const context = useContext(SocialStatusContext);
  if (!context) {
    throw new Error('useSocialStatus must be used within a SocialStatusProvider');
  }
  return context;
};

export const SocialStatusProvider = ({ children }) => {
  const { makeRequest } = useApi();
  
  // Cache state
  const [cachedStatus, setCachedStatus] = useState({
    connections: {
      instagram: false,
      linkedin: false,
      facebook: false,
      x: false
    },
    profiles: {
      instagram: null,
      linkedin: null,
      facebook: null,
      x: null
    },
    lastFetched: null,
    isLoading: false
  });

  // Use ref to avoid infinite re-renders
  const cacheRef = useRef(cachedStatus);
  cacheRef.current = cachedStatus;

  // Cache duration: 5 minutes
  const CACHE_DURATION = 5 * 60 * 1000;

  const isDataFresh = useCallback(() => {
    if (!cachedStatus.lastFetched) return false;
    return Date.now() - cachedStatus.lastFetched < CACHE_DURATION;
  }, [cachedStatus.lastFetched, CACHE_DURATION]);

  const fetchSocialStatus = useCallback(async (force = false) => {
    // Use ref to avoid dependency issues
    const currentCache = cacheRef.current;
    const now = Date.now();
    const dataIsFresh = currentCache.lastFetched && (now - currentCache.lastFetched < CACHE_DURATION);
    
    // Return cached data if it's fresh and not forced
    if (!force && dataIsFresh) {
      console.log('[SocialStatus] Using cached data');
      return {
        connections: currentCache.connections,
        profiles: currentCache.profiles
      };
    }

    console.log('[SocialStatus] Fetching fresh data from API');
    
    try {
      setCachedStatus(prev => ({ ...prev, isLoading: true }));

      const response = await makeRequest('/api/v1/social/status', {
        method: 'GET'
      });

      console.log('[SocialStatus] API response:', response);

      // Parse the response
      const connections = {};
      const profiles = {};

      if (response.platforms) {
        Object.keys(response.platforms).forEach(platform => {
          const platformData = response.platforms[platform];
          connections[platform] = platformData.connected || false;
          profiles[platform] = platformData.connected ? {
            username: platformData.username,
            account_type: platformData.account_type,
            token_valid: platformData.token_valid,
            expires_at: platformData.expires_at
          } : null;
        });
      }

      // Set defaults for platforms not in response
      const defaultPlatforms = ['instagram', 'linkedin', 'facebook', 'x'];
      defaultPlatforms.forEach(platform => {
        if (!(platform in connections)) {
          connections[platform] = false;
          profiles[platform] = null;
        }
      });

      // Update cache
      setCachedStatus({
        connections,
        profiles,
        lastFetched: now,
        isLoading: false
      });

      return { connections, profiles };
    } catch (error) {
      console.error('[SocialStatus] Error fetching status:', error);
      setCachedStatus(prev => ({ ...prev, isLoading: false }));
      
      // Return cached data on error if available
      const currentCacheAfterError = cacheRef.current;
      if (currentCacheAfterError.lastFetched) {
        return {
          connections: currentCacheAfterError.connections,
          profiles: currentCacheAfterError.profiles
        };
      }
      
      throw error;
    }
  }, [makeRequest, CACHE_DURATION]);

  const invalidateCache = useCallback(() => {
    console.log('[SocialStatus] Cache invalidated');
    setCachedStatus(prev => ({ ...prev, lastFetched: null }));
  }, []);

  const updateConnectionStatus = useCallback((platform, connected, profile = null) => {
    console.log(`[SocialStatus] Updating ${platform} connection:`, { connected, profile });
    setCachedStatus(prev => ({
      ...prev,
      connections: {
        ...prev.connections,
        [platform]: connected
      },
      profiles: {
        ...prev.profiles,
        [platform]: profile
      }
    }));
  }, []);

  // Create a ref to store the fetch function
  const fetchSocialStatusRef = useRef();
  fetchSocialStatusRef.current = fetchSocialStatus;

  // Create a stable fetch function that doesn't cause re-renders
  const stableFetchSocialStatus = useCallback(async (force = false) => {
    return fetchSocialStatusRef.current(force);
  }, []);

  const value = {
    // Data
    connections: cachedStatus.connections,
    profiles: cachedStatus.profiles,
    isLoading: cachedStatus.isLoading,
    lastFetched: cachedStatus.lastFetched,
    
    // Methods
    fetchSocialStatus: stableFetchSocialStatus,
    invalidateCache,
    updateConnectionStatus,
    isDataFresh: isDataFresh()
  };

  return (
    <SocialStatusContext.Provider value={value}>
      {children}
    </SocialStatusContext.Provider>
  );
};